/* eslint-env browser */
/**
 * Collection filter behavior — desktop dropdowns + mobile sheet.
 *
 * Architecture:
 *   - All event listeners are document-level (delegation) so they survive
 *     re-rendering of the section innerHTML.
 *   - Filter changes are routed through navigateTo(url), which:
 *       1. Adds .loading to .product-grid (skeleton state)
 *       2. Fetches /collections/x?...&sections=<sectionId>
 *       3. Replaces the contents of [data-collection-section]
 *       4. Pushes the new URL via history.pushState
 *   - Without JS, the form's submit button + native filter anchor links
 *     do the same job via full page reloads.
 */
(function () {
  if (window.__OS_COLLECTION_FILTERS__) return;
  window.__OS_COLLECTION_FILTERS__ = true;

  var SECTION_SELECTOR = '[data-collection-section]';
  var FETCH_DEBOUNCE_MS = 200;
  var SKELETON_MIN_MS = 250;

  function getRoot() { return document.querySelector(SECTION_SELECTOR); }
  function getSectionId() {
    var root = getRoot();
    return root ? root.getAttribute('data-section-id') : null;
  }

  /* ─── Section re-render via ?sections= ─────────────────────────────── */
  var inFlight = null;
  function navigateTo(url) {
    var sectionId = getSectionId();
    if (!sectionId) { window.location.href = url; return; }

    var grid = document.querySelector('.product-grid');
    if (grid) grid.classList.add('loading');

    // Preserve sheet open state across section swap
    var sheetEl = document.getElementById('filterSheet');
    var sheetWasOpen = sheetEl && sheetEl.getAttribute('aria-hidden') === 'false';

    var startedAt = Date.now();
    var u = new URL(url, window.location.origin);
    // Strip any stale sections= param (pagination markup picks up everything
    // from the request URL, which means a fetch URL can leak into hrefs).
    u.searchParams.delete('sections');
    var fetchUrl = u.pathname + (u.search ? u.search + '&' : '?') + 'sections=' + encodeURIComponent(sectionId);

    if (inFlight && inFlight.abort) inFlight.abort();
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    inFlight = ctrl;

    fetch(fetchUrl, { signal: ctrl ? ctrl.signal : undefined, headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (data) {
        var html = data && data[sectionId];
        if (!html) throw new Error('No section in response');

        var root = getRoot();
        if (!root) return;

        // Browsers don't run <script> tags inserted via innerHTML, but the
        // whole section's listeners are document-level so we don't care.
        // Strip the outer wrapper element from the response (Shopify returns
        // a wrapper div); we just want its inner content.
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var inner = holder.querySelector(SECTION_SELECTOR) || holder;

        root.replaceWith(inner);
        markJsEnabled();
        if (sheetWasOpen) openSheet();

        var elapsed = Date.now() - startedAt;
        if (elapsed < SKELETON_MIN_MS) {
          setTimeout(function () {
            var g = document.querySelector('.product-grid');
            if (g) g.classList.remove('loading');
          }, SKELETON_MIN_MS - elapsed);
        }

        if (window.history && window.history.pushState) {
          window.history.pushState({ collectionFilter: true }, '', u.pathname + u.search);
        }

        // Scroll grid into view if user is below it (filter bar context shouldn't move)
        var bar = document.querySelector('.filter-bar');
        if (bar) {
          var rect = bar.getBoundingClientRect();
          if (rect.top < 0) {
            bar.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // Soft fallback: full nav
        window.location.href = url;
      });
  }

  /* ─── Build URL from form state ────────────────────────────────────── */
  function urlFromForm(form) {
    var action = form.getAttribute('action') || window.location.pathname;
    var u = new URL(action, window.location.origin);
    // Reset existing query string — form is the source of truth.
    u.search = '';

    var data = new FormData(form);
    data.forEach(function (val, key) {
      if (val === '' || val == null) return;
      u.searchParams.append(key, val);
    });
    return u.pathname + '?' + u.searchParams.toString();
  }

  var formChangeTimer = null;
  function scheduleFormSubmit(form) {
    if (formChangeTimer) clearTimeout(formChangeTimer);
    formChangeTimer = setTimeout(function () {
      formChangeTimer = null;
      navigateTo(urlFromForm(form));
    }, FETCH_DEBOUNCE_MS);
  }

  /* ─── Document delegation ──────────────────────────────────────────── */

  // Mark JS as enabled — hides the "Apply" submit button
  document.addEventListener('DOMContentLoaded', markJsEnabled);
  if (document.readyState !== 'loading') markJsEnabled();
  function markJsEnabled() {
    var form = document.querySelector('[data-filter-form]');
    if (form) form.classList.add('js-on');
  }

  // 1. Dropdown trigger toggles
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.dd-trigger');
    if (trigger && trigger.closest('[data-dropdown]')) {
      e.preventDefault();
      var dd = trigger.closest('[data-dropdown]');
      var willOpen = !dd.classList.contains('open');
      closeAllDropdowns();
      if (willOpen) {
        dd.classList.add('open');
        trigger.setAttribute('aria-expanded', 'true');
      }
      return;
    }
    // Click inside a panel → don't close
    if (e.target.closest('.dd-panel')) return;
    // Otherwise, click outside any dropdown → close
    closeAllDropdowns();
  });

  function closeAllDropdowns() {
    document.querySelectorAll('[data-dropdown].open').forEach(function (d) {
      d.classList.remove('open');
      var t = d.querySelector('.dd-trigger');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  // 2. Keyboard handlers (Esc closes panels/sheet; arrows navigate options)
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var sheet = document.getElementById('filterSheet');
      if (sheet && sheet.getAttribute('aria-hidden') === 'false') {
        closeSheet();
      } else {
        closeAllDropdowns();
      }
      return;
    }

    // Arrow keys within a panel — works for both checkbox lists (filters)
    // and sort-option button lists.
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      var panel = e.target.closest('.dd-panel');
      if (!panel) return;
      e.preventDefault();
      var items = Array.from(panel.querySelectorAll('input.dd-checkbox, .sort-option'));
      if (!items.length) return;
      var idx = items.indexOf(document.activeElement);
      if (idx === -1) idx = 0;
      var next = e.key === 'ArrowDown'
        ? (idx + 1) % items.length
        : (idx - 1 + items.length) % items.length;
      items[next].focus();
    }
  });

  // 3. Filter form changes (checkboxes; sort fallback select fires submit
  // via inline onchange="form.requestSubmit()" → caught by the submit handler)
  document.addEventListener('change', function (e) {
    var form = e.target.closest('[data-filter-form]');
    if (!form) return;
    if (e.target.matches('[data-filter-input]')) {
      scheduleFormSubmit(form);
    }
  });

  // 4. Submit (no-JS fallback, but we still intercept)
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-filter-form]');
    if (!form) return;
    e.preventDefault();
    if (formChangeTimer) { clearTimeout(formChangeTimer); formChangeTimer = null; }
    navigateTo(urlFromForm(form));
  });

  // 5. Filter anchor links — chips, sheet options, clear-all, empty-state CTA
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-filter-link]');
    if (!link) return;
    // Allow modifier-clicks to behave normally (open in new tab, etc.)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.target && link.target !== '' && link.target !== '_self') return;
    e.preventDefault();
    navigateTo(link.getAttribute('href'));
  });

  // 5b. Sort option click — single-select. Update aria + check, sync the
  // hidden fallback <select> so FormData picks it up, then navigate.
  document.addEventListener('click', function (e) {
    var opt = e.target.closest('.sort-option');
    if (!opt) return;
    e.preventDefault();
    var dd = opt.closest('[data-sort-dropdown]');
    if (!dd) return;

    dd.querySelectorAll('.sort-option').forEach(function (o) {
      var selected = (o === opt);
      o.setAttribute('aria-selected', selected ? 'true' : 'false');
      var check = o.querySelector('.sort-check');
      if (check) check.textContent = selected ? '✓' : '';
    });

    var labelEl = opt.querySelector('span');
    var triggerVal = dd.querySelector('[data-sort-value-display]');
    if (labelEl && triggerVal) triggerVal.textContent = labelEl.textContent.trim();

    var form = opt.closest('[data-filter-form]');
    if (form) {
      var src = form.querySelector('[data-sort-source]');
      if (src) src.value = opt.getAttribute('data-sort-value');
    }

    closeAllDropdowns();
    if (form) navigateTo(urlFromForm(form));
  });

  // 6. Pagination links — also re-render via section swap
  document.addEventListener('click', function (e) {
    var pag = e.target.closest('.pagination a');
    if (!pag) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigateTo(pag.getAttribute('href'));
  });

  /* ─── Mobile sheet accordion ───────────────────────────────────────── */
  // Click an accordion header → toggle its panel; collapse other accordions
  // in the same sheet (single-open mode). Tap is purely UI — no navigation.
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.sheet-acc-trigger');
    if (!trigger) return;
    e.preventDefault();
    var sheet = trigger.closest('.filter-sheet');
    if (!sheet) return;
    var willOpen = trigger.getAttribute('aria-expanded') !== 'true';
    sheet.querySelectorAll('.sheet-acc-trigger').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
      var panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = true;
    });
    if (willOpen) {
      trigger.setAttribute('aria-expanded', 'true');
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (panel) panel.hidden = false;
    }
  });

  // Sheet sort-option click — same behavior as desktop sort: update the
  // hidden fallback select, close the sheet, navigate.
  document.addEventListener('click', function (e) {
    var opt = e.target.closest('.sheet-sort-option');
    if (!opt) return;
    e.preventDefault();
    var val = opt.getAttribute('data-sort-value');
    var form = document.querySelector('[data-filter-form]');
    if (!form) return;
    var src = form.querySelector('[data-sort-source]');
    if (src) src.value = val;
    closeSheet();
    navigateTo(urlFromForm(form));
  });

  /* ─── Mobile bottom-sheet ──────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-sheet-open]')) {
      e.preventDefault();
      openSheet();
      return;
    }
    if (e.target.closest('[data-sheet-close]')) {
      e.preventDefault();
      closeSheet();
    }
  });

  var lastFocused = null;

  // Initialize closed-sheet inert state (sheet ships aria-hidden="true" by default)
  (function initSheetInert() {
    var sheet = document.getElementById('filterSheet');
    if (sheet && sheet.getAttribute('aria-hidden') !== 'false') {
      sheet.setAttribute('inert', '');
    }
  })();

  function openSheet() {
    var sheet = document.getElementById('filterSheet');
    if (!sheet) return;
    lastFocused = document.activeElement;
    sheet.setAttribute('aria-hidden', 'false');
    sheet.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    var trigger = document.querySelector('[data-sheet-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    // Reset accordion state to defaults: filters with active values
    // start expanded; everything else collapsed (including Sort).
    sheet.querySelectorAll('.sheet-acc-trigger').forEach(function (t) {
      var defaultOpen = t.getAttribute('data-default-open') === 'true';
      t.setAttribute('aria-expanded', defaultOpen ? 'true' : 'false');
      var panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !defaultOpen;
    });

    // Move focus into the sheet
    var first = sheet.querySelector('.filter-sheet-close');
    if (first && first.focus) first.focus();
  }
  function closeSheet() {
    var sheet = document.getElementById('filterSheet');
    if (!sheet) return;
    sheet.setAttribute('aria-hidden', 'true');
    sheet.setAttribute('inert', '');
    document.body.style.overflow = '';
    var trigger = document.querySelector('[data-sheet-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (lastFocused && lastFocused.focus) {
      lastFocused.focus();
      lastFocused = null;
    }
  }

  // Focus trap inside the sheet
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var sheet = document.getElementById('filterSheet');
    if (!sheet || sheet.getAttribute('aria-hidden') !== 'false') return;
    var focusables = sheet.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ─── Browser back/forward ─────────────────────────────────────────── */
  window.addEventListener('popstate', function (ev) {
    if (ev.state && ev.state.collectionFilter) {
      navigateTo(window.location.pathname + window.location.search);
    }
  });
})();
